import { Button, Divider, Grid, Paper, Stack, TablePagination } from '@mui/material'
import DialogAlert from 'components/Dialog/Alert'
import { useDialog } from 'components/Dialog/hooks/alert.hook'
import Label from 'components/Label'
import { usePagination } from 'hooks/pagination.hook'
import TableData from './components/TableData'
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import familiarReferenceService from 'services/familiar-reference.service'
import { familiarReferenceInit, FamiliarReferenceType } from 'types/familiar-reference.type'
import { PageableProps } from 'types/pageable.type'
import { ActionType, DialogForm } from './components/DialogForm'
import { EmptyList } from './components/EmptyList'
import { useSnackBar } from '../../../../contexts/snackbar.context'
import { useBackdrop } from '../../../../contexts/backdrop.context'
import { HttpErrorType } from '../../../../types/http.type'
import { SystemMessages } from 'utils/enums/system-message.enum'
import { errorColor, errorMessage } from 'utils/error.util'

export const TabFamiliarReference: React.FC = () => {
  const { id: teenagerId } = useParams()

  const {
    page,
    elements: { totalOfElements, handleTotalOfElements },
    rows: { rowsPerPage, rowsOptions },
    handleNewPage,
    handleRowsPerPage,
  } = usePagination()

  const {
    openDialog,
    handleOpenDialog: handleOpenDialogSuccess,
    handleCloseDialog: handleCloseDialogSuccess,
    handleChangeTitle,
    title,
  } = useDialog()

  const {
    openDialog: openExcluded,
    handleOpenDialog: handleOpenDialogChoice,
    handleCloseDialog: handleCloseDialogChoice,
  } = useDialog()

  const { showSnackBar } = useSnackBar()
  const { handleBackdropOpen, handleBackdropClose } = useBackdrop()

  const [loading, setLoading] = useState(false)
  const [actionType, setActionType] = useState<ActionType>('save')
  const [reference, setReference] = useState<FamiliarReferenceType>(familiarReferenceInit)
  const [data, setData] = useState<FamiliarReferenceType[]>([])

  const getAllFamiliarReferences = useCallback(async (): Promise<void> => {
    if (!teenagerId) return
    const pageable: PageableProps = {
      pageSize: rowsPerPage,
      page,
    }
    handleBackdropOpen()
    await familiarReferenceService
      .getAll(teenagerId, pageable)
      .then((response) => {
        if (response) {
          setData(response.data.content)
          handleTotalOfElements(response.data.totalElements)
        }
      })
      .catch((error: HttpErrorType) => showSnackBar(errorMessage(error), errorColor(error)))
      .finally(() => handleBackdropClose())
  }, [teenagerId, page, rowsPerPage])

  const saveFamiliarReference = useCallback(
    async (data: FamiliarReferenceType): Promise<void> => {
      setLoading(true)
      if (!teenagerId || !data) return

      if (!data.id) {
        await familiarReferenceService
          .create(teenagerId, data)
          .then(() => showSnackBar(SystemMessages.MESSAGE_001, 'success'))
          .catch((error: HttpErrorType) => showSnackBar(errorMessage(error), errorColor(error)))
          .finally(() => handleCloseDialogSuccess())
      } else {
        await familiarReferenceService
          .update(data.id, data)
          .then(() => showSnackBar(SystemMessages.MESSAGE_001, 'success'))
          .catch((error: HttpErrorType) => showSnackBar(errorMessage(error), errorColor(error)))
          .finally(() => handleCloseDialogSuccess())
      }
      await getAllFamiliarReferences()
      handleBackdropClose()
      setLoading(false)
    },
    [teenagerId, handleCloseDialogSuccess, getAllFamiliarReferences],
  )

  const handleOpenEdit = useCallback(
    (data: FamiliarReferenceType): void => {
      handleChangeTitle('EDITAR REFERÊNCIA FAMILÍAR')
      handleOpenDialogSuccess()
      setActionType('save')
      setReference(data)
    },
    [handleChangeTitle, handleOpenDialogSuccess, setActionType, setReference],
  )

  const handleOpenVisualize = useCallback(
    (data: FamiliarReferenceType): void => {
      handleChangeTitle('VISUALIZAR REFERÊNCIA FAMILÍAR')
      handleOpenDialogSuccess()
      setActionType('visualize')
      setReference(data)
    },
    [handleChangeTitle, handleOpenDialogSuccess, setActionType, setReference],
  )

  const handleOpenDelete = useCallback(
    (data: FamiliarReferenceType): void => {
      handleOpenDialogChoice()
      setReference(data)
    },
    [handleOpenDialogChoice, setReference],
  )

  const handleEdit = useCallback((): void => {
    handleChangeTitle('EDITAR REFERÊNCIA FAMILÍAR')
    setActionType('save')
  }, [handleChangeTitle, setActionType])

  const handleCancelDialog = useCallback((): void => {
    handleCloseDialogSuccess()
    setReference(familiarReferenceInit)
  }, [handleCloseDialogSuccess, setReference])

  const deleteFamiliarReference = async (data: FamiliarReferenceType): Promise<void> => {
    if (!data.id) return

    handleBackdropOpen()
    await familiarReferenceService
      .delete(data.id)
      .then(() => {
        getAllFamiliarReferences()
        showSnackBar(SystemMessages.MESSAGE_001, 'success')
      })
      .catch((error: HttpErrorType) => showSnackBar(errorMessage(error), errorColor(error)))
      .finally(() => handleBackdropClose())
  }

  const handleOpenNew = (): void => {
    handleChangeTitle('INCLUIR REFERÊNCIA FAMILÍAR')
    handleOpenDialogSuccess()
    setActionType('save')
    setReference(familiarReferenceInit)
  }

  const handleDelete = (): void => {
    deleteFamiliarReference(reference)
    handleCloseDialogChoice()
    handleCloseDialogSuccess()
  }

  useEffect(() => {
    getAllFamiliarReferences()
  }, [getAllFamiliarReferences])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack direction='column'>
          <Stack direction='row' sx={{ mb: 1 }}>
            <Label text='Referência familiar ' v='h6' color='primary' bold flexGrow />
            <Button variant='contained' color='secondary' onClick={handleOpenNew}>
              Cadastrar
            </Button>
          </Stack>
          <Divider variant='fullWidth' />
        </Stack>
      </Grid>
      {data ? (
        <Grid item xs={12}>
          {data.length > 0 ? (
            <Paper variant='outlined'>
              <TableData
                data={data}
                onVisualize={handleOpenVisualize}
                onEdit={handleOpenEdit}
                onDelete={handleOpenDelete}
              />
              <TablePagination
                labelRowsPerPage='Linhas por página:'
                labelDisplayedRows={({ from, to, count }) =>
                  `Linhas ${from}-${to} de um total de ${count} itens.`
                }
                rowsPerPageOptions={rowsOptions}
                count={totalOfElements}
                rowsPerPage={rowsPerPage}
                onPageChange={handleNewPage}
                page={page}
                component='div'
                onRowsPerPageChange={handleRowsPerPage}
              />
            </Paper>
          ) : (
            <EmptyList />
          )}
        </Grid>
      ) : (
        <EmptyList />
      )}
      <DialogForm
        type={actionType}
        title={title}
        open={openDialog}
        data={reference}
        onSave={saveFamiliarReference}
        onEdit={handleEdit}
        onDelete={handleOpenDelete}
        onCancel={handleCancelDialog}
        loading={loading}
      />
      <DialogAlert
        title='Excluir'
        contentText='Tem certeza que deseja excluir a Referência Familiar?'
        open={openExcluded}
        onSuccess={handleDelete}
        onClose={handleCloseDialogChoice}
      />
    </Grid>
  )
}
